import React from 'react'

import { VERSION_TABLES } from '../constants'
import {
    ActiveRow,
    Counter,
    ErrorBox,
    FormHeader,
    FormSubtitle,
    FormTitle,
    Panel,
    PreviewHeader,
    PreviewMeta,
    PreviewSection,
    PreviewSub,
    RightCell,
    StateBox,
    Table,
    TablePicker,
    TableWrapper,
    ViewButton,
} from '../style'
import { useHistoryVersionsTab } from './useHistoryVersionsTab'

const VersionPreview = ({
    activeVersion,
    versionTable,
    versionData,
    versionDataLoading,
    versionDataError,
    previewRows,
    previewColumns,
    formatDate,
    formatCellValue,
    onChangeTable,
}) => {
    if (!activeVersion) return null

    return (
        <PreviewSection>
            <PreviewHeader>
                Версия: {activeVersion.label || activeVersion.version_id}{' '}
                <PreviewSub>({formatDate(activeVersion.published_at)})</PreviewSub>
            </PreviewHeader>

            <TablePicker>
                <label htmlFor="history-version-table">Таблица</label>
                <select
                    id="history-version-table"
                    value={versionTable}
                    onChange={(event) => onChangeTable(event.target.value)}
                >
                    {VERSION_TABLES.map((table) => (
                        <option key={table} value={table}>
                            {table}
                        </option>
                    ))}
                </select>
            </TablePicker>

            {versionDataError && <ErrorBox>{versionDataError}</ErrorBox>}

            {versionDataLoading ? (
                <StateBox>Загрузка содержимого...</StateBox>
            ) : versionData.length === 0 ? (
                <StateBox>В этой таблице нет данных</StateBox>
            ) : (
                <>
                    <PreviewMeta>
                        Всего записей: {versionData.length}
                        {versionData.length > previewRows.length
                            && ` (показаны первые ${previewRows.length})`}
                    </PreviewMeta>
                    <TableWrapper>
                        <Table>
                            <thead>
                                <tr>
                                    {previewColumns.map((column) => (
                                        <th key={column}>{column}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previewRows.map((row, index) => (
                                    <tr key={index}>
                                        {previewColumns.map((column) => (
                                            <td key={column}>{formatCellValue(row[column])}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableWrapper>
                </>
            )}
        </PreviewSection>
    )
}

const HistoryVersionsTab = () => {
    const {
        versions,
        maxVersions,
        versionsLoading,
        versionsError,
        versionData,
        versionDataLoading,
        versionDataError,
        activeVersion,
        activeVersionId,
        versionTable,
        previewRows,
        previewColumns,
        formatDate,
        formatCellValue,
        handleViewVersion,
        handleChangeVersionTable,
    } = useHistoryVersionsTab()

    return (
        <Panel>
            <FormHeader>
                <div>
                    <FormTitle>История версий расписания</FormTitle>
                    <FormSubtitle>
                        Опубликованные версии расписания. Откройте любую, чтобы
                        посмотреть её содержимое
                    </FormSubtitle>
                </div>
                <Counter>{versions.length} / {maxVersions}</Counter>
            </FormHeader>

            {versionsError && <ErrorBox>{versionsError}</ErrorBox>}

            {versionsLoading ? (
                <StateBox>Загрузка версий...</StateBox>
            ) : versions.length === 0 ? (
                <StateBox>Пока нет опубликованных версий</StateBox>
            ) : (
                <TableWrapper>
                    <Table>
                        <thead>
                            <tr>
                                <th>Дата публикации</th>
                                <th>Название</th>
                                <th>Автор</th>
                                <th>Семестр</th>
                                <th>Учебный год</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {versions.map((version) => {
                                const isActive = activeVersionId === version.version_id
                                const RowComponent = isActive ? ActiveRow : 'tr'

                                return (
                                    <RowComponent key={version.version_id}>
                                        <td>{formatDate(version.published_at)}</td>
                                        <td>{version.label || version.version_id}</td>
                                        <td>{version.published_by || '—'}</td>
                                        <td>{version.semester ?? '—'}</td>
                                        <td>{version.academic_year || '—'}</td>
                                        <RightCell>
                                            <ViewButton
                                                type="button"
                                                onClick={() => handleViewVersion(version)}
                                            >
                                                {isActive ? 'Скрыть' : 'Просмотреть'}
                                            </ViewButton>
                                        </RightCell>
                                    </RowComponent>
                                )
                            })}
                        </tbody>
                    </Table>
                </TableWrapper>
            )}

            <VersionPreview
                activeVersion={activeVersion}
                versionTable={versionTable}
                versionData={versionData}
                versionDataLoading={versionDataLoading}
                versionDataError={versionDataError}
                previewRows={previewRows}
                previewColumns={previewColumns}
                formatDate={formatDate}
                formatCellValue={formatCellValue}
                onChangeTable={handleChangeVersionTable}
            />
        </Panel>
    )
}

export default HistoryVersionsTab
